/* eslint-disable @next/next/no-img-element */
"use client";

import React, { useEffect, useState, useRef } from "react";
import Link from "next/link";
import CommonButton from "../CommonButton/CommonButton";
import { usePathname } from "next/navigation";
import DropdownMenu from "./DropdownMenu";
import MobileMenu from "./MobileMenu";

const NewHeader = () => {
    const currentPage = usePathname();
    const dropdownRefs = useRef({});

    const [scrollPosition, setScrollPosition] = useState(0);
    const [openDropdown, setOpenDropdown] = useState(null);

    const handleHoverToggle = (menu, show) => {
        // Only allow hover to toggle if no dropdown is actively clicked open
        if (!openDropdown || openDropdown === menu) {
            setOpenDropdown(show ? menu : null);
        }
    };

    const handleClickToggle = (menu) => {
        setOpenDropdown((prev) => (prev === menu ? null : menu));
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            let shouldClose = true;
            for (const menu in dropdownRefs.current) {
                if (
                    dropdownRefs.current[menu] &&
                    dropdownRefs.current[menu].contains(event.target)
                ) {
                    shouldClose = false;
                    break;
                }
            }
            if (shouldClose) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setScrollPosition(document.documentElement.scrollTop);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const isSpecialPage =
        currentPage.split("/")[1] === "games" ||
        currentPage.split("/")[1] === "dapp-store" ||
        currentPage.split("/")[1] === "game-animation" ||
        currentPage.split("/")[1] === "game-art" ||
        currentPage.split("/")[1] === "game-resources" ||
        currentPage.split("/")[1] === "nfts" ||
        currentPage.split("/")[1] === "defi" ||
        currentPage.split("/")[1] === "ai" ||
        currentPage.split("/")[1] === "metaverse" ||
        currentPage.split("/")[1] === "blockchain" ||
        currentPage.split("/")[1] === "marketing" ||
        currentPage.split("/")[1] === "crypto" ||
        currentPage.split("/")[1] === "ico" ||
        currentPage.split("/")[1] === "sto" ||
        currentPage.split("/")[1] === "ido" ||
        currentPage.split("/")[1] === "web3" ||
        (currentPage.split("/")[1] === "blog" && (currentPage.split("/").length === 2 || currentPage.split("/").length > 3)) ||
        currentPage.split("/")[1] === "author" ||
        currentPage.split("/")[1] === "evm" ||
        currentPage.split("/")[1] === "portfolio";

    const navTextColor = isSpecialPage ? scrollPosition < 30 ? "text-white" : "text-black" : "text-black";
    const headerBgClass = currentPage === "/" ? "bg-white" : scrollPosition < 30 ? "bg-transparent" : "bg-white";
    const logoSrc = isSpecialPage ? scrollPosition < 30 ? "/Mainlogo.webp" : "/MainLogo-dark.svg" : "/MainLogo-dark.svg";

    return (
        <header className={`fixed top-0 left-0 right-0 z-[99999] ${currentPage === "/" ? "home-shadow" : ""}`}>
            <div className={`hidden md:block ${headerBgClass} ${navTextColor}`}>
                <div className="md:flex hidden lg:container mx-auto MainMenu lg:!px-4 md:!px-3">
                    <Link href="/" className="flex items-center">
                        <img src={logoSrc} alt="Tanthetaa-logo" className="w-24 lg:w-28 xl:w-32 cursor-pointer" width={128} height={40} priority />
                    </Link>

                    <div className="Menu flex flex-wrap justify-center items-center gap-1 lg:gap-4 xl:gap-6">
                        <DropdownMenu
                            title="Services"
                            menuKey="services"
                            navTextColor={navTextColor}
                            isOpen={openDropdown === "services"}
                            onHoverToggle={handleHoverToggle}
                            onClickToggle={handleClickToggle}
                            dropdownRef={(ref) => (dropdownRefs.current["services"] = ref)}
                            mainItems="blockchainMainItem"
                            subCategoryMap={{
                                1: { columns: "NFT1, NFT2, NFT3" },
                                2: { columns: "EVM1" },
                                3: { columns: "blockchain1, blockchain2" },
                                4: { columns: "metaverse1" },
                                5: { columns: "web3_1, web3_2" },
                                6: { columns: "crypto1, crypto2" },
                                7: { columns: "defi1, defi2, defi3" },
                                8: { columns: "GenerativeAI1" },
                                9: { columns: "ico1, sto1, ido1" },
                                10: { columns: "marketing1" },
                            }}
                        />

                        <DropdownMenu
                            title="Games"
                            menuKey="games"
                            navTextColor={navTextColor}
                            isOpen={openDropdown === "games"}
                            onHoverToggle={handleHoverToggle}
                            onClickToggle={handleClickToggle}
                            dropdownRef={(ref) => (dropdownRefs.current["games"] = ref)}
                            mainItems="gameMainItem"
                            subCategoryMap={{
                                1: { columns: "gameDevelopment1, gameDevelopment2, gameDevelopment3" },
                                2: { columns: "gameArt1, gameArt2" },
                                3: { columns: "animation1" },
                            }}
                        />

                        <DropdownMenu
                            title="Industries"
                            menuKey="industries"
                            navTextColor={navTextColor}
                            isOpen={openDropdown === "industries"}
                            onHoverToggle={handleHoverToggle}
                            onClickToggle={handleClickToggle}
                            dropdownRef={(ref) => (dropdownRefs.current["industries"] = ref)}
                            mainItems="industriesMainItem"
                            subCategoryMap={{
                                1: { columns: "onDemand1" },
                                2: { columns: "foodRestaurant1" },
                                3: { columns: "bankingFinance1" },
                                4: { columns: "ecommerce1, ecommerce2" },
                                5: { columns: "healthcareFitness1, healthcareFitness2" },
                                6: { columns: "education1, education2" },
                            }}
                        />

                        <div>
                            <span>
                                <Link href="/portfolio" className={`menuTitle flex MenuList !font-manrope ${navTextColor} transition-colors duration-200 xl:text-[17px] lg:text-base text-sm xl:py-2.5 xl:px-4 py-2 px-1`}>
                                    Case Studies
                                </Link>
                            </span>
                        </div>

                        <div>
                            <span>
                                <Link href="/blog" className={`menuTitle flex MenuList !font-manrope ${navTextColor} transition-colors duration-200 xl:text-[17px] lg:text-base text-sm xl:py-2.5 xl:px-4 py-2 px-1`}>
                                    Blog
                                </Link>
                            </span>
                        </div>

                        <DropdownMenu
                            title="About Us"
                            menuKey="about"
                            navTextColor={navTextColor}
                            isOpen={openDropdown === "about"}
                            onHoverToggle={handleHoverToggle}
                            onClickToggle={handleClickToggle}
                            dropdownRef={(ref) => (dropdownRefs.current["about"] = ref)}
                            mainItems="aboutMainItem"
                            subCategoryMap={{
                                1: { columns: "companyData1, companyData2" },
                                2: { columns: "projectCosting1" },
                                3: { columns: "dedicatedResources1, dedicatedResources2, dedicatedResources3" },
                            }}
                        />
                    </div>

                    <div>
                        <CommonButton
                            href={`/contact`}
                            name={`Contact Us`}
                            className={"common-blue-button-style"}
                        />
                    </div>
                </div>
            </div>

            <MobileMenu
                navTextColor={navTextColor}
                scrollPosition={scrollPosition}
            />
        </header>
    );
};

export default NewHeader;
